import api from '@/services/ApiService';
import {
  Box,
  Button,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Alert,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useRouteLoaderData } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import logo from '@/assets/logo.svg';
import { useTheme, useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import { Card } from '@mui/material';
import dayjs from 'dayjs';

export default function NfcTools() {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up('sm'));
  const { users, me } = useRouteLoaderData('auth');
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [events, setEvents] = useState(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const formatTime = (d) => dayjs(d).format('DD.MM.YYYY HH:mm');

  useEffect(() => {
    async function getTool() {
      const { data } = await api.get(`/tool/${id}`);

      const now = new Date();
      const horizon = new Date(now);
      horizon.setDate(horizon.getDate() + 1);

      const {
        data: { result: eventList = [] },
      } = await api.get(
        `/event?tools=${id}&end[$gt]=${now.toISOString()}&start[$lt]=${horizon.toISOString()}`,
      );

      setTool(data);
      setEvents(eventList);
    }
    getTool();
  }, [id]);

  const reservationOwnerIds = useMemo(() => {
    const ids = new Set();
    for (const ev of events || []) {
      const candidates = [ev?.reservedBy, ev?.owner, ev?.user];
      candidates.forEach((c) => {
        const id = typeof c === 'string' ? c : c?._id || c?.id;
        if (id) ids.add(id);
      });

      const arrays = [ev?.users];
      arrays.forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((p) => {
            const id = typeof p === 'string' ? p : p?._id || p?.id;
            if (id) ids.add(id);
          });
        }
      });
    }
    return ids;
  }, [events]);

  const reservationUsers = useMemo(() => {
    try {
      return (users || []).filter((u) => reservationOwnerIds.has(u._id));
    } catch {
      return [];
    }
  }, [users, reservationOwnerIds]);

  const reservationUserNameById = useMemo(
    () =>
      Object.fromEntries(
        (reservationUsers || []).map((u) => [
          u._id,
          `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        ]),
      ),
    [reservationUsers],
  );

  if (!tool)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          px: 4,
        }}
      >
        <Skeleton
          variant="rounded"
          width={sm ? 500 : 350}
          height="600px"
          sx={{ borderRadius: 3 }}
        />
      </Box>
    );
  const hasReservation = Array.isArray(events) && events.length > 0;
  const totalCount =
    tool.items?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
  const takenBy = users.find((u) => u._id === tool.takenBy);
  const isMaintenance = tool.status === 'MAINTENANCE';
  const isTaken = tool.status === 'TAKEN';
  const isAvailable = tool.status === 'AVAILABLE';

  const updateToolStatus = async (
    newStatus,
    newTakenBy,
    newTakenAt,
    comment,
  ) => {
    try {
      const returnedAt =
        newStatus === 'AVAILABLE' ? new Date().toISOString() : null;

      await api.put(`/tool/${id}`, {
        status: newStatus,
        takenBy: newTakenBy,
        takenAt: newTakenAt,
        returnedAt,
        content: comment || '',
      });

      setTool((prev) => ({
        ...prev,
        status: newStatus,
        takenBy: newTakenBy,
        takenAt: newTakenAt,
        returnedAt,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleTake = async () => {
    if (!me?._id || isMaintenance || !isAvailable) return;

    if (hasReservation) {
      toast.warn(
        'Achtung: Dieses Werkzeug ist reserviert. Du nimmst es trotzdem mit.',
        {
          position: 'bottom-right',
          autoClose: 2000,
        },
      );
    }

    await updateToolStatus(
      'TAKEN',
      me._id,
      new Date().toISOString(),
      returnComment.trim(),
    );

    setReturnComment('');
    setIsConfirmed(false);
  };

  const handleReturnClick = () => {
    handleConfirmReturn(returnComment.trim());
  };

  const handleConfirmReturn = async (comment = '') => {
    if (!comment.trim() && tool.takenBy !== me?._id) {
      toast.warning(
        'Bitte Kommentar eingeben, da du nicht der ursprüngliche Nutzer bist.',
        { position: 'bottom-right', autoClose: 1500 },
      );
      return;
    }

    await updateToolStatus('AVAILABLE', null, null, comment.trim());

    if (comment.trim()) {
      toast.success('Kommentar wurde gespeichert und Tool zurückgelegt!', {
        position: 'bottom-right',
        autoClose: 1500,
      });
    }

    setReturnDialogOpen(false);
    setReturnComment('');
    setIsConfirmed(false);
  };

  return (
    <Card
      sx={{
        boxShadow: 4,
        p: 2,
        width: '100%',
        maxWidth: sm ? 500 : 350,
        mx: 'auto',
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: sm ? 4 : 2,
            mb: 2,
            width: '100%',
            maxWidth: sm ? 500 : 350,
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{ width: '100px', height: 'auto' }}
          />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: sm ? '30px' : '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tool.title}
            </Typography>

            <Chip
              label={
                tool.status === 'MAINTENANCE'
                  ? 'Nicht verfügbar'
                  : tool.status === 'TAKEN'
                    ? `Mitgenommen: ${takenBy?.firstName} ${takenBy?.lastName}`
                    : 'Verfügbar im Lager'
              }
              icon={tool.status === 'MAINTENANCE' ? <CloseIcon /> : undefined}
              variant="outlined"
              color={
                tool.status === 'AVAILABLE'
                  ? 'success'
                  : tool.status === 'TAKEN'
                    ? 'warning'
                    : tool.status === 'MAINTENANCE'
                      ? 'error'
                      : 'default'
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                minHeight: 20,
                mt: 1,
                mb: 1,
                fontSize: sm ? '16px' : '14px',
                textAlign: 'center',
              }}
            />
          </Box>
        </Box>
        <TextField
          label="Bemerkung"
          multiline
          minRows={5}
          sx={{ width: '100%' }}
          value={returnComment}
          onChange={(e) => setReturnComment(e.target.value)}
        />
        {tool.items?.length > 0 && (
          <Box
            sx={{
              display: 'block',
              flexDirection: 'column',
              gap: 1,
              mt: 2,
              width: '100%',
            }}
          >
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography component="span">
                  Untergeordnetes Werkzeug ({totalCount})
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  maxHeight: 100,
                  overflowY: 'auto',
                }}
              >
                {tool.items.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 0.4,
                      borderBottom: '1px solid black',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '17px' }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold', fontSize: '17px' }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
            />
          }
          label="Hiermit bestätige ich, dass das Werkzeug vollständig ist!"
          sx={{
            display: 'flex',
            mt: 2,
            '& .MuiFormControlLabel-label': {
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
            },
          }}
        />

        {hasReservation && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: sm ? '16px' : '14px',
                mb: 0.5,
              }}
            >
              Bereits reserviert von:
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: sm ? 0.4 : 0.2,
              }}
            >
              {(events || [])
                .filter((ev) => ev?.start && ev?.end)
                .sort((a, b) => new Date(a.start) - new Date(b.start))
                .slice(0, 3)
                .map((ev) => {
                  const raw = ev?.reservedBy ?? ev?.owner ?? ev?.user;
                  const userId =
                    typeof raw === 'string' ? raw : raw?._id || raw?.id;
                  const name = reservationUserNameById[userId] || 'Unbekannt';

                  return (
                    <Typography
                      key={ev._id || ev.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mb: 0.8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: sm ? '15px' : '14px',
                          lineHeight: 1.2,
                        }}
                      >
                        {name}
                      </span>

                      <span
                        style={{
                          fontSize: sm ? '14px' : '12px',
                          opacity: 0.85,
                          marginTop: 2,
                          lineHeight: 1.2,
                        }}
                      >
                        {formatTime(ev.start)} – {formatTime(ev.end)}
                      </span>
                    </Typography>
                  );
                })}
            </Box>
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 2,
            width: '100%',
            flexDirection: 'column',
          }}
        >
          {isTaken ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleReturnClick}
              sx={{ fontSize: '16px' }}
              disabled={!isConfirmed}
            >
              Zurücklegen
            </Button>
          ) : (
            <Button
              variant="contained"
              color={hasReservation ? 'error' : 'primary'}
              onClick={handleTake}
              sx={{ fontSize: '16px' }}
              disabled={isMaintenance || !isAvailable || !isConfirmed}
            >
              {hasReservation ? 'Trotzdem mitnehmen' : 'Mitnehmen'}
            </Button>
          )}
        </Box>

        <Button
          variant="outlined"
          color="primary"
          sx={{ display: 'flex', width: '100%', mt: 1, fontSize: '16px' }}
          onClick={() => {
            toast.error('Dann kümmer dich drum!', {
              position: 'bottom-right',
              autoClose: 1500,
            });
          }}
        >
          Problem melden
        </Button>
        <Dialog
          open={returnDialogOpen}
          onClose={() => setReturnDialogOpen(false)}
        >
          <DialogTitle>Kommentar beim Zurücklegen</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Kommentar"
              fullWidth
              multiline
              minRows={3}
              value={returnComment}
              onChange={(e) => setReturnComment(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReturnDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => handleConfirmReturn(returnComment)}
              variant="contained"
            >
              Zurücklegen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Card>
  );
}
